"use client";

import React, { useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useSummary } from "@/lib/contexts/ai-summary-context";
import type { AISummaryRequest } from "@/lib/types/ai";
import { cn } from "@/lib/utils";
import {
  type AISummaryProps,
  resolveSummaryPresentation,
  ShimmerLoader,
  SummaryContent,
  StreamingContent,
  CompactView,
} from "./ai-summary/index";
import {
  EmptySummaryPrompt,
  SummaryCardHeader,
} from "./ai-summary/SummaryCardHeader";
import { useSummaryDisplay } from "./ai-summary/useSummaryDisplay";

// ============================================================================
// AI SUMMARY COMPONENT
// ============================================================================
//
// PURPOSE: Reusable component for displaying AI-generated summaries
//
// FEATURES:
// - Streaming text display with typewriter effect
// - Loading states and error handling
// - Refresh and clear functionality
// - Customizable appearance
// - Metadata display (activity count, processing time)
// - Auto-generation on mount (optional)
//
// USAGE:
// <AISummary
//   contactId="uuid"
//   summaryType="timeline"
//   options={{ daysBack: 30 }}
//   autoGenerate={true}
//   className="custom-class"
// />
//
// WHERE THE PARTS LIVE: this file is the composition and the request shape. The
// display state (what text is on screen, whether it is expanded, the progress
// line) is `./ai-summary/useSummaryDisplay`; the per-entity copy it chooses from
// is `./ai-summary/generation-narration`; the header and empty-state prompt are
// `./ai-summary/SummaryCardHeader`.
//
// ============================================================================

// Re-export types for convenience
export type { AISummaryProps } from "./ai-summary/index";

export function AISummary({
  contactId,
  entityType,
  entityId,
  summaryType,
  options = {},
  autoGenerate = false,
  showMetadata = true,
  showRefreshButton = true,
  showClearButton = false,
  compact = false,
  className,
  onSummaryGenerated,
  onError,
}: AISummaryProps) {
  // Memoize the request to prevent unnecessary re-renders
  // Request shape should stay aligned with @/lib/domain/crm-entity-relationships (contact vs company summary scope).
  const request: AISummaryRequest = useMemo(
    () => ({
      entityType: entityType || (contactId ? "contact" : undefined),
      entityId: entityId || contactId,
      contactId, // for back-compat
      summaryType,
      options,
    }),
    [entityType, entityId, contactId, summaryType, options]
  );

  const { summary, isLoading, isStreaming, error, hasError, generate, refresh, clear } = useSummary(
    request,
    {
      autoGenerate,
      onError: (message) => {
        console.error("Auto-generate error:", message);
        onError?.(message);
      },
    }
  );

  const presentation = useMemo(
    () => resolveSummaryPresentation(summaryType, entityType),
    [summaryType, entityType]
  );

  const display = useSummaryDisplay({
    content: summary?.content,
    isLoading,
    isStreaming,
    error,
    entityType,
    summaryType,
    onSummaryGenerated,
    onError,
  });
  const { displayedContent, resetForGeneration } = display;

  // A rejection here is already surfaced by the context's `error` state, so it is
  // LOGGED rather than re-reported — but it must still be caught, or the void'd
  // promise becomes an unhandled rejection.
  const handleGenerate = useCallback(() => {
    resetForGeneration();
    void generate().catch((err: unknown) => {
      console.error("Generate error:", err);
    });
  }, [generate, resetForGeneration]);

  const handleRefresh = useCallback(() => {
    resetForGeneration();
    void refresh().catch((err: unknown) => {
      console.error("Refresh error:", err);
    });
  }, [refresh, resetForGeneration]);

  const handleClear = useCallback(() => {
    resetForGeneration();
    clear();
  }, [clear, resetForGeneration]);

  const busy = isLoading || isStreaming;
  const resolvedEntityType = entityType || (contactId ? "contact" : "");
  const resolvedEntityId = entityId || contactId;

  if (compact) {
    return (
      <CompactView
        Icon={presentation.icon}
        config={presentation}
        displayedContent={displayedContent}
        isLoading={isLoading}
        isStreaming={isStreaming}
        isTyping={false}
        hasError={hasError}
        error={error}
        summary={summary}
        showRefreshButton={showRefreshButton}
        onGenerate={handleGenerate}
        onRefresh={handleRefresh}
        className={className}
      />
    );
  }

  return (
    <Card className={cn("w-full", className)}>
      <SummaryCardHeader
        presentation={presentation}
        busy={busy}
        shareableMarkdown={summary?.content && !busy ? summary.content : null}
        entityType={resolvedEntityType || undefined}
        entityId={resolvedEntityId}
        showRefreshButton={showRefreshButton}
        showClearButton={showClearButton}
        hasSummary={Boolean(summary)}
        onRefresh={handleRefresh}
        onGenerate={handleGenerate}
        onClear={handleClear}
      />

      <CardContent className="space-y-4">
        {hasError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Waiting for the first token — shimmer only until content arrives */}
        {busy && !displayedContent && <ShimmerLoader statusMessage={display.statusMessage} />}

        {/* Live streaming view — render tokens as they arrive so the user can
            start reading before generation finishes */}
        {isStreaming && displayedContent && <StreamingContent content={displayedContent} />}

        {/* Completed summary — collapsible teaser */}
        {displayedContent && !busy && (
          <SummaryContent
            displayedContent={displayedContent}
            isExpanded={display.isExpanded}
            onToggleExpanded={display.toggleExpanded}
            showMetadata={showMetadata}
            summary={summary}
          />
        )}

        {/* Generate button — only when there is nothing to show and nothing wrong */}
        {!summary && !isLoading && !hasError && (
          <EmptySummaryPrompt
            briefingKind={display.briefingKind}
            disabled={isLoading}
            onGenerate={handleGenerate}
          />
        )}
      </CardContent>
    </Card>
  );
}
